// Storage access can throw a SecurityError in some TWA/WebView contexts (e.g.
// when Chrome's storage access is restricted by device privacy settings or when
// assetlinks.json verification fails). We cascade through three tiers so the
// session stays as durable as the environment allows:
//   1. localStorage  — persists across tabs and restarts (normal case)
//   2. sessionStorage — persists across page refreshes within the same tab
//   3. in-memory Map  — survives in-app navigation only (last resort)

const memory = new Map<string, string>()

type StorageTier = "local" | "session" | "memory"
let tier: StorageTier | null = null

function probe(storage: Storage, key: string): boolean {
  storage.setItem(key, key)
  storage.removeItem(key)
  return true
}

function resolveTier(): StorageTier {
  if (tier !== null) return tier
  const k = "__fp_test__"
  try {
    if (probe(window.localStorage, k)) return (tier = "local")
  } catch {}
  try {
    if (probe(window.sessionStorage, k)) return (tier = "session")
  } catch {}
  return (tier = "memory")
}

function getStorage(): Storage | null {
  switch (resolveTier()) {
    case "local":
      return window.localStorage
    case "session":
      return window.sessionStorage
    default:
      return null
  }
}

const safeStorage = {
  getItem(key: string): string | null {
    const s = getStorage()
    return s ? s.getItem(key) : memory.get(key) ?? null
  },
  setItem(key: string, value: string): void {
    const s = getStorage()
    if (s) {
      s.setItem(key, value)
    } else {
      memory.set(key, value)
    }
  },
  removeItem(key: string): void {
    const s = getStorage()
    if (s) {
      s.removeItem(key)
    } else {
      memory.delete(key)
    }
  },
  clear(): void {
    const s = getStorage()
    if (s) {
      s.clear()
    } else {
      memory.clear()
    }
  },
}

export default safeStorage
